import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Home } from "lucide-react";
import Navbar from "../components/lac/Navbar";
import Footer from "../components/lac/Footer";
import { api, formatApiError } from "../lib/api";

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | unpaid | error
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("Missing session id.");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("unpaid");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setStatus("unpaid");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(formatApiError(e));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-xl mx-auto px-6 pt-40 pb-32 text-center" data-testid="booking-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#D946EF]" />
            <h1 className="mt-6 font-display font-bold text-3xl tracking-tighter">Confirming payment…</h1>
            <p className="mt-3 text-zinc-400 font-body">Hold tight — checking with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-[#D946EF]/10 border border-[#D946EF]/40 flex items-center justify-center pulse-glow">
              <CheckCircle2 className="w-10 h-10 text-[#D946EF]" strokeWidth={2} />
            </div>
            <h1 className="mt-8 font-display font-bold text-4xl sm:text-5xl tracking-tighter">Deposit received.</h1>
            <p className="mt-5 text-zinc-300 font-body leading-relaxed">
              Your slot is locked in. We've emailed your confirmation and we'll be in touch
              before your install. Your <span className="text-[#D946EF] font-medium">£{((details?.amount_total || 5000)/100).toFixed(2)}</span> deposit comes off your final invoice on the day.
            </p>
            <Link to="/" className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D946EF] text-white font-semibold hover:bg-[#F0ABFC] transition" data-testid="success-home-btn">
              <Home className="w-4 h-4" /> Back home
            </Link>
          </>
        )}
        {(status === "unpaid" || status === "error") && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-yellow-500/10 border border-yellow-500/40 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-yellow-400" strokeWidth={2} />
            </div>
            <h1 className="mt-8 font-display font-bold text-3xl tracking-tighter">Payment not completed</h1>
            <p className="mt-4 text-zinc-400 font-body">{error || "We couldn't verify your payment yet. If money was taken, contact us on WhatsApp 07538 963090 with your name and we'll sort it manually."}</p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/booking" className="px-6 py-3 rounded-full bg-[#D946EF] text-white font-semibold hover:bg-[#F0ABFC] transition">Try again</Link>
              <a href="https://wa.me/447538963090" target="_blank" rel="noopener noreferrer" className="px-6 py-3 rounded-full border border-white/15 text-white hover:border-[#25D366] hover:text-[#25D366] transition">WhatsApp us</a>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
